import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  Save,
  CheckCircle,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Building2,
  Users,
  ClipboardCheck,
  Send,
  Loader2,
  ShieldCheck,
  FileText,
  MapPin,
  Calendar,
  Info,
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Progress } from '../components/ui/Progress';
import { Spinner } from '../components/ui/Spinner';
import { ConfirmModal } from '../components/ui/Modal';
import { MultiPartQuestionCard } from '../components/assessment/MultiPartQuestionCard';
import { OnboardingDiagnostic, type DiagnosticTotals } from '../components/assessment/OnboardingDiagnostic';
import { getChecklistForState, getStateOptions, requiresStateLicense } from '../config/checklist';
import {
  getDiagnosticQuestionByItemId,
  evaluateDiagnosticAnswers,
} from '../config/diagnostic/generator';
import type { SubQuestionAnswer } from '../types/diagnostic';
import { calculateProgress, isAssessmentComplete, getUnknownCount, getUnknownItems } from '../lib/scoring';
import { cn } from '../lib/utils';
import {
  getSnapshotForAssessment,
  updateSnapshotProgress,
  submitAssessment as submitAssessmentApi,
} from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import type { ChecklistResponse, AgencyProfile, OnboardingAssessment, ChecklistStatus, ChecklistCategory } from '../types';

// ============================================
// CONSTANTS
// ============================================

type Step = 'state' | 'profile' | 'diagnostic' | 'checklist' | 'review';

const STEPS: { id: Step; label: string; shortLabel: string; icon: React.ElementType }[] = [
  { id: 'state', label: 'Select State', shortLabel: 'State', icon: MapPin },
  { id: 'profile', label: 'Agency Info', shortLabel: 'Info', icon: Building2 },
  { id: 'diagnostic', label: 'Initial Assessment', shortLabel: 'Assessment', icon: Users },
  { id: 'checklist', label: 'Compliance Checklist', shortLabel: 'Checklist', icon: ClipboardCheck },
  { id: 'review', label: 'Review & Submit', shortLabel: 'Review', icon: Send },
];

const STATUS_OPTIONS: { value: ChecklistStatus; label: string; icon: React.ElementType; color: string; bgColor: string }[] = [
  { value: 'COMPLIANT', label: 'Compliant', icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50 border-green-200 hover:bg-green-100' },
  { value: 'AT_RISK', label: 'At Risk', icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' },
  { value: 'NON_COMPLIANT', label: 'Non-Compliant', icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50 border-red-200 hover:bg-red-100' },
  { value: 'UNKNOWN', label: 'Unknown', icon: HelpCircle, color: 'text-gray-500', bgColor: 'bg-gray-50 border-gray-200 hover:bg-gray-100' },
];

// Service types offered by home care agencies
const SERVICE_OPTIONS = [
  { value: 'personal_care', label: 'Personal Care / Companion' },
  { value: 'homemaker', label: 'Homemaker Services' },
  { value: 'skilled_nursing', label: 'Skilled Nursing (RN/LPN)' },
  { value: 'home_health_aide', label: 'Home Health Aide' },
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'occupational_therapy', label: 'Occupational Therapy' },
  { value: 'speech_therapy', label: 'Speech Therapy' },
  { value: 'respiratory_therapy', label: 'Respiratory Therapy' },
  { value: 'medical_social_work', label: 'Medical Social Work' },
];

// No-license state explanations
const NO_LICENSE_STATE_INFO: Record<string, string> = {
  'AL': 'Alabama does not require state licensure for home health agencies. Medicare certification is required for agencies providing skilled services.',
  'MI': 'Michigan does not require a state license for non-medical home care agencies. Medicare certification is required for skilled nursing services.',
  'ID': 'Idaho eliminated the home health agency license requirement effective July 1, 2025. Medicare certification is still required for skilled services.',
};

// ============================================
// EXTENDED AGENCY PROFILE TYPE
// ============================================

interface ExtendedAgencyProfile extends Partial<AgencyProfile> {
  // Basic Info
  name?: string;
  state?: string;
  contactEmail?: string;
  contactPhone?: string;
  numberOfStaff?: number;
  yearsInOperation?: number;
  
  // Licensing (for licensed states)
  licenseNumber?: string;
  licenseExpirationDate?: string;
  
  // Certification (for all states)
  medicareCertified?: boolean;
  medicareProviderNumber?: string;
  medicaidProvider?: boolean;
  medicaidProviderNumber?: string;
  
  // Business (for no-license states)
  businessRegistrationNumber?: string;
  
  // Services
  servicesOffered?: string[];
  providesSkilled?: boolean;
  
  // Additional
  lastSurveyDate?: string;
  hasOpenDeficiencies?: boolean;
}

// ============================================
// COMPONENT
// ============================================

export function Assessment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const snapshotId = searchParams.get('snapshotId');
  const { success: showSuccess, error: showError } = useToast();

  // Loading & error states
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Policy acknowledgment
  const [hasAcknowledgedPolicy, setHasAcknowledgedPolicy] = useState(false);
  
  // Navigation
  const [currentStep, setCurrentStep] = useState<Step>('state');
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  
  // Save states
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Form state
  const [agencyProfile, setAgencyProfile] = useState<ExtendedAgencyProfile>({
    state: '',
    servicesOffered: [],
    medicareCertified: false,
    medicaidProvider: false,
    providesSkilled: false,
    hasOpenDeficiencies: false,
  });
  const [onboarding, setOnboarding] = useState<Partial<OnboardingAssessment>>({});
  const [responses, setResponses] = useState<Record<string, ChecklistResponse>>({});
  const [diagnosticAnswers, setDiagnosticAnswers] = useState<Record<string, SubQuestionAnswer[]>>({});
  const [onboardingDiagnosticAnswers, setOnboardingDiagnosticAnswers] = useState<Record<string, string | string[] | number | null>>({});

  // State change confirmation modal
  const [stateChangeConfirm, setStateChangeConfirm] = useState<{
    isOpen: boolean;
    fromState: string;
    toState: string;
  }>({ isOpen: false, fromState: '', toState: '' });

  // Validation state
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [diagnosticTotals, setDiagnosticTotals] = useState<DiagnosticTotals>({
    total: 0,
    answered: 0,
    compliant: 0,
    atRisk: 0,
    nonCompliant: 0,
  });

  // Track expanded checklist item (for accordion behavior)
  const [expandedChecklistItemId, setExpandedChecklistItemId] = useState<string | null>(null);

  // Derived state
  const stateRequiresLicense = agencyProfile.state ? requiresStateLicense(agencyProfile.state) : true;
  const checklist: ChecklistCategory[] = agencyProfile.state
    ? getChecklistForState(agencyProfile.state)
    : [];
  const progress = calculateProgress(responses, checklist);
  const currentCategory = checklist[currentCategoryIndex];

  // Track unsaved changes
  const hasUnsavedChanges = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to navigate steps with scroll-to-top (instant snap)
  const goToStep = useCallback((step: Step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // ============================================
  // LOAD SNAPSHOT DATA
  // ============================================

  useEffect(() => {
    async function loadSnapshot() {
      if (!snapshotId) {
        setLoadError('No snapshot ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getSnapshotForAssessment(snapshotId);

        // Populate form state
        if (data.agencyProfile) {
          setAgencyProfile(data.agencyProfile as ExtendedAgencyProfile);
          // If state is already set, skip state selection step
          if ((data.agencyProfile as ExtendedAgencyProfile).state) {
            setCurrentStep('profile');
          }
        }
        if (data.onboardingAssessment) {
          setOnboarding(data.onboardingAssessment as Partial<OnboardingAssessment>);
        }
        if (data.checklistResponses) {
          const formattedResponses: Record<string, ChecklistResponse> = {};
          for (const [itemId, response] of Object.entries(data.checklistResponses)) {
            formattedResponses[itemId] = {
              itemId,
              status: response.status as ChecklistStatus,
              notes: response.notes,
              updatedAt: new Date(),
            };
          }
          setResponses(formattedResponses);
        }
        if (data.diagnosticAnswers) {
          setDiagnosticAnswers(data.diagnosticAnswers as Record<string, SubQuestionAnswer[]>);
        }
        if (data.onboardingDiagnosticAnswers) {
          setOnboardingDiagnosticAnswers(data.onboardingDiagnosticAnswers as Record<string, string | string[] | null>);
        }
        // Skip policy screen if assessment already started
        if (data.assessmentStarted) {
          setHasAcknowledgedPolicy(true);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load assessment';
        setLoadError(message);
        console.error('Failed to load snapshot:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadSnapshot();
  }, [snapshotId]);

  // ============================================
  // AUTO-SAVE
  // ============================================

  const saveProgress = useCallback(async () => {
    if (!snapshotId || !hasUnsavedChanges.current) return;

    setIsSaving(true);
    try {
      const checklistResponses: Record<string, { status: string; notes?: string }> = {};
      for (const [itemId, response] of Object.entries(responses)) {
        checklistResponses[itemId] = {
          status: response.status,
          notes: response.notes,
        };
      }

      await updateSnapshotProgress({
        snapshotId,
        agencyProfile: agencyProfile as Record<string, unknown>,
        onboardingAssessment: onboarding as Record<string, unknown>,
        checklistResponses,
        diagnosticAnswers: diagnosticAnswers as Record<string, unknown>,
        onboardingDiagnosticAnswers: onboardingDiagnosticAnswers as Record<string, unknown>,
        assessmentStarted: hasAcknowledgedPolicy,
      });

      hasUnsavedChanges.current = false;
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [snapshotId, agencyProfile, onboarding, responses, diagnosticAnswers, onboardingDiagnosticAnswers, hasAcknowledgedPolicy]);

  // Trigger auto-save on data change
  useEffect(() => {
    if (isLoading) return;

    hasUnsavedChanges.current = true;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveProgress();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [agencyProfile, onboarding, responses, diagnosticAnswers, onboardingDiagnosticAnswers, isLoading, saveProgress]);

  // ============================================
  // HANDLERS
  // ============================================

  // ============================================
  // VALIDATION HELPERS
  // ============================================

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};

    if (!agencyProfile.name?.trim()) {
      errors.name = 'Agency name is required';
    }
    if (!agencyProfile.contactEmail?.trim()) {
      errors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(agencyProfile.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getProfileEncouragements = (): string[] => {
    const warnings: string[] = [];

    if (stateRequiresLicense && !agencyProfile.licenseNumber?.trim()) {
      warnings.push('License number helps us provide more accurate compliance insights');
    }
    if (stateRequiresLicense && !agencyProfile.licenseExpirationDate) {
      warnings.push('License expiration date helps track renewal compliance');
    }
    if (!agencyProfile.servicesOffered?.length) {
      warnings.push('Selecting services offered helps tailor the assessment');
    }

    return warnings;
  };

  const handleStateChange = (newState: string) => {
    const currentState = agencyProfile.state;

    // If no change, just return
    if (newState === currentState) return;

    // Check if there's any existing assessment data
    const hasExistingData =
      Object.keys(responses).length > 0 ||
      Object.keys(diagnosticAnswers).length > 0 ||
      Object.keys(onboardingDiagnosticAnswers).length > 0;

    // If changing state with existing data, show confirmation modal
    if (currentState && hasExistingData) {
      setStateChangeConfirm({
        isOpen: true,
        fromState: currentState,
        toState: newState,
      });
      return;
    }

    // Update the state and clear state-specific fields
    // (license fields, dates, business registration - these vary by state)
    setAgencyProfile(p => ({
      ...p,
      state: newState,
      // Clear state-specific licensing fields
      licenseNumber: '',
      licenseExpirationDate: '',
      businessRegistrationNumber: '',
      // Clear survey history (state-specific)
      lastSurveyDate: '',
    }));
  };

  const confirmStateChange = () => {
    // Reset all assessment data
    setResponses({});
    setDiagnosticAnswers({});
    setOnboardingDiagnosticAnswers({});
    setOnboarding({});
    setCurrentCategoryIndex(0);

    // Reset agency profile but keep the new state
    setAgencyProfile({
      state: stateChangeConfirm.toState,
      servicesOffered: [],
      medicareCertified: false,
      medicaidProvider: false,
      providesSkilled: false,
      hasOpenDeficiencies: false,
    });

    // Go back to profile step to fill in new info
    setCurrentStep('profile');
  };

  const handleResponseChange = (itemId: string, status: ChecklistStatus) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: {
        itemId,
        status,
        updatedAt: new Date(),
        notes: prev[itemId]?.notes,
      },
    }));
    // Expand this item (accordion behavior)
    setExpandedChecklistItemId(itemId);
  };

  const handleNotesChange = (itemId: string, notes: string) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        itemId,
        status: prev[itemId]?.status || 'UNKNOWN',
        notes,
        updatedAt: new Date(),
      },
    }));
  };

  const handleDiagnosticAnswerChange = (
    itemId: string,
    answers: SubQuestionAnswer[]
  ) => {
    setDiagnosticAnswers(prev => ({
      ...prev,
      [itemId]: answers,
    }));

    const question = getDiagnosticQuestionByItemId(itemId.replace(/^[A-Z]{2}-/, ''), agencyProfile.state || 'VA');
    if (question) {
      const result = evaluateDiagnosticAnswers(question, answers);
      setResponses(prev => ({
        ...prev,
        [itemId]: {
          itemId,
          status: result.status,
          updatedAt: new Date(),
        },
      }));
    }
  };

  const handleServiceToggle = (serviceValue: string) => {
    setAgencyProfile(prev => {
      const current = prev.servicesOffered || [];
      const updated = current.includes(serviceValue)
        ? current.filter(s => s !== serviceValue)
        : [...current, serviceValue];
      
      // Auto-detect if they provide skilled services
      const skilledServices = ['skilled_nursing', 'physical_therapy', 'occupational_therapy', 'speech_therapy', 'respiratory_therapy'];
      const providesSkilled = updated.some(s => skilledServices.includes(s));
      
      return {
        ...prev,
        servicesOffered: updated,
        providesSkilled,
      };
    });
  };

  const handleManualSave = async () => {
    setIsSaving(true);
    try {
      const checklistResponses: Record<string, { status: string; notes?: string }> = {};
      for (const [itemId, response] of Object.entries(responses)) {
        checklistResponses[itemId] = {
          status: response.status,
          notes: response.notes,
        };
      }

      await updateSnapshotProgress({
        snapshotId: snapshotId!,
        agencyProfile: agencyProfile as Record<string, unknown>,
        onboardingAssessment: onboarding as Record<string, unknown>,
        checklistResponses,
        diagnosticAnswers: diagnosticAnswers as Record<string, unknown>,
        onboardingDiagnosticAnswers: onboardingDiagnosticAnswers as Record<string, unknown>,
        assessmentStarted: hasAcknowledgedPolicy,
      });

      hasUnsavedChanges.current = false;
      setLastSaved(new Date());
      showSuccess('Progress saved');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!isAssessmentComplete(responses, checklist)) {
      showError('Please answer all checklist items before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      await handleManualSave();
      // Pass state-specific expected total to backend
      await submitAssessmentApi(snapshotId!, progress.total);
      showSuccess('Assessment submitted successfully!');
      navigate(`/results?snapshotId=${snapshotId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit';
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // RENDER: LOADING STATE
  // ============================================

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600">Loading assessment...</p>
          </div>
        </main>
      </div>
    );
  }

  // ============================================
  // RENDER: ERROR STATE
  // ============================================

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Assessment
            </h2>
            <p className="text-gray-600 mb-4">{loadError}</p>
            <Link to="/">
              <Button>Go Home</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ============================================
  // RENDER: POLICY ACKNOWLEDGMENT
  // ============================================

  if (!hasAcknowledgedPolicy) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center py-12 mt-16">
          <Container size="sm">
            <Card>
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-teal-600" />
                </div>
                <CardTitle className="text-2xl">Before You Begin</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-600 text-center">
                  You're about to start your state-specific compliance assessment.
                  This will take approximately 15 minutes. You can save and resume anytime.
                </p>

                {/* What to expect */}
                <div className="bg-teal-50 rounded-lg p-5 border border-teal-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                    <h3 className="font-semibold text-teal-800">What to expect</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-teal-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span>Answer questions about your agency's current compliance practices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span>See your risk score instantly — full PDF report emailed to you</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-3">
                <Button
                  onClick={async () => {
                    setHasAcknowledgedPolicy(true);
                    try {
                      await updateSnapshotProgress({
                        snapshotId: snapshotId!,
                        assessmentStarted: true,
                      });
                    } catch (err) {
                      console.error('Failed to save assessment started:', err);
                    }
                  }}
                  disabled={false}
                  className="w-full"
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >
                  Begin Assessment
                </Button>
                <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
                  Return to Home
                </Link>
              </CardFooter>
            </Card>
          </Container>
        </main>
      </div>
    );
  }

  // ============================================
  // RENDER: MAIN ASSESSMENT
  // ============================================

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 py-4 mt-16">
        <Container>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Assessment Progress
            </span>
            <div className="flex items-center gap-4">
              {isSaving && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
              {lastSaved && !isSaving && (
                <span className="text-sm text-gray-400">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              <span className="text-sm text-gray-500">
                {progress.answered} of {progress.total} items
              </span>
            </div>
          </div>
          <Progress value={progress.percentage} showLabel={false} />
        </Container>
      </div>

      {/* Step Indicator */}
      <div className="bg-white border-b border-gray-200 py-3 sm:py-4">
        <Container>
          <div className="flex items-center justify-between gap-1 sm:gap-2 overflow-x-auto">
            {STEPS.map((step, index) => {
              const isActive = currentStep === step.id;
              const isPast = STEPS.findIndex(s => s.id === currentStep) > index;
              // Disable if no state selected, OR if trying to access review without completing checklist
              const isDisabled = (step.id !== 'state' && !agencyProfile.state) ||
                (step.id === 'review' && progress.answered < progress.total);

              return (
                <button
                  key={step.id}
                  onClick={() => !isDisabled && goToStep(step.id)}
                  disabled={isDisabled}
                  className={cn(
                    'flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-colors whitespace-nowrap shrink-0',
                    isActive
                      ? ''
                      : isPast
                        ? 'text-teal-600 hover:bg-teal-50'
                        : isDisabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:bg-gray-50'
                  )}
                  style={isActive ? { backgroundColor: 'rgba(0, 150, 136, 0.1)', color: '#009688' } : undefined}
                >
                  <step.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden md:inline font-medium text-sm">{step.label}</span>
                  <span className="md:hidden font-medium text-xs sm:text-sm">{step.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </Container>
      </div>

      <main className="flex-1 py-8">
        <Container size="lg">
          
          {/* ============================================ */}
          {/* STEP 1: STATE SELECTION */}
          {/* ============================================ */}
          
          {currentStep === 'state' && (
            <Card>
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-teal-600" />
                </div>
                <CardTitle className="text-2xl">Select Your State</CardTitle>
                <p className="text-gray-500 mt-2">
                  Your state determines which compliance requirements apply to your agency.
                </p>
              </CardHeader>
              <CardContent className="max-w-md mx-auto">
                <Select
                  label="State"
                  value={agencyProfile.state || ''}
                  onChange={e => handleStateChange(e.target.value)}
                  options={[
                    { value: '', label: 'Select your state...' },
                    ...getStateOptions(),
                  ]}
                />
                
                {/* No-License State Notice */}
                {agencyProfile.state && !stateRequiresLicense && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-blue-800">
                          {agencyProfile.state === 'AL' && 'Alabama'} 
                          {agencyProfile.state === 'MI' && 'Michigan'} 
                          {agencyProfile.state === 'ID' && 'Idaho'} Does Not Require State Licensure
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          {NO_LICENSE_STATE_INFO[agencyProfile.state]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* State Selected Confirmation */}
                {agencyProfile.state && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 text-center">
                      Your assessment will be based on <strong>{getStateOptions().find(s => s.value === agencyProfile.state)?.label}</strong> regulations
                      {stateRequiresLicense 
                        ? ' and state licensing requirements.'
                        : ' and Medicare certification requirements.'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="justify-center">
                <Button
                  onClick={() => goToStep('profile')}
                  disabled={!agencyProfile.state}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">Continue to Agency Info</span>
                  <span className="sm:hidden">Continue</span>
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* ============================================ */}
          {/* STEP 2: AGENCY PROFILE */}
          {/* ============================================ */}
          
          {currentStep === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle>Agency Information</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Tell us about your agency. This information helps us tailor the assessment to your situation.
                </p>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Basic Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-teal-600" />
                    Basic Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Input
                        label="Agency Name"
                        placeholder="Your Home Care Agency"
                        value={agencyProfile.name || ''}
                        onChange={e => {
                          setAgencyProfile(p => ({ ...p, name: e.target.value }));
                          if (profileErrors.name) setProfileErrors(p => ({ ...p, name: '' }));
                        }}
                        required
                        error={profileErrors.name}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-2 bg-gray-100 rounded-lg text-gray-700 font-medium">
                          {getStateOptions().find(s => s.value === agencyProfile.state)?.label}
                        </span>
                        <button
                          onClick={() => goToStep('state')}
                          className="text-sm text-teal-600 hover:text-teal-700"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                    <div>
                      <Input
                        label="Contact Email"
                        type="email"
                        placeholder="admin@youragency.com"
                        value={agencyProfile.contactEmail || ''}
                        onChange={e => {
                          setAgencyProfile(p => ({ ...p, contactEmail: e.target.value }));
                          if (profileErrors.contactEmail) setProfileErrors(p => ({ ...p, contactEmail: '' }));
                        }}
                        required
                        error={profileErrors.contactEmail}
                      />
                    </div>
                    <Input
                      label="Contact Phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={agencyProfile.contactPhone || ''}
                      onChange={e => setAgencyProfile(p => ({ ...p, contactPhone: e.target.value }))}
                    />
                    <Input
                      label="Number of Staff"
                      type="number"
                      placeholder="10"
                      value={agencyProfile.numberOfStaff || ''}
                      onChange={e => setAgencyProfile(p => ({ ...p, numberOfStaff: parseInt(e.target.value) || 0 }))}
                    />
                    <Input
                      label="Years in Operation"
                      type="number"
                      placeholder="5"
                      value={agencyProfile.yearsInOperation || ''}
                      onChange={e => setAgencyProfile(p => ({ ...p, yearsInOperation: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>

                {/* Licensing Section - Only for Licensed States */}
                {stateRequiresLicense && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-600" />
                      State Licensing
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="State License Number"
                        placeholder="Enter your license number"
                        value={agencyProfile.licenseNumber || ''}
                        onChange={e => setAgencyProfile(p => ({ ...p, licenseNumber: e.target.value }))}
                      />
                      <Input
                        label="License Expiration Date"
                        type="date"
                        value={agencyProfile.licenseExpirationDate || ''}
                        onChange={e => setAgencyProfile(p => ({ ...p, licenseExpirationDate: e.target.value }))}
                      />
                    </div>
                  </div>
                )}

                {/* Business Registration - Only for No-License States */}
                {!stateRequiresLicense && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-600" />
                      Business Registration
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="Business Registration Number"
                        placeholder="State business registration"
                        value={agencyProfile.businessRegistrationNumber || ''}
                        onChange={e => setAgencyProfile(p => ({ ...p, businessRegistrationNumber: e.target.value }))}
                      />
                    </div>
                  </div>
                )}

                {/* Certification Section */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-teal-600" />
                    Federal Certification
                  </h3>
                  <div className="space-y-4">
                    {/* Medicare */}
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agencyProfile.medicareCertified || false}
                          onChange={e => setAgencyProfile(p => ({ ...p, medicareCertified: e.target.checked }))}
                          className="mt-1 w-5 h-5 rounded border-gray-300 accent-teal-600"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Medicare Certified</span>
                          <p className="text-sm text-gray-500">
                            Your agency is certified by CMS to provide Medicare-covered services
                          </p>
                        </div>
                      </label>
                      {agencyProfile.medicareCertified && (
                        <div className="mt-4 ml-8">
                          <Input
                            label="Medicare Provider Number (CCN)"
                            placeholder="12-3456"
                            value={agencyProfile.medicareProviderNumber || ''}
                            onChange={e => setAgencyProfile(p => ({ ...p, medicareProviderNumber: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>

                    {/* Medicaid */}
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agencyProfile.medicaidProvider || false}
                          onChange={e => setAgencyProfile(p => ({ ...p, medicaidProvider: e.target.checked }))}
                          className="mt-1 w-5 h-5 rounded border-gray-300 accent-teal-600"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Medicaid Provider</span>
                          <p className="text-sm text-gray-500">
                            Your agency is enrolled as a Medicaid provider in your state
                          </p>
                        </div>
                      </label>
                      {agencyProfile.medicaidProvider && (
                        <div className="mt-4 ml-8">
                          <Input
                            label="Medicaid Provider Number"
                            placeholder="Enter provider number"
                            value={agencyProfile.medicaidProviderNumber || ''}
                            onChange={e => setAgencyProfile(p => ({ ...p, medicaidProviderNumber: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services Offered */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    Services Offered
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Select all services your agency provides. This helps us show relevant compliance items.
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {SERVICE_OPTIONS.map(service => {
                      const isSelected = agencyProfile.servicesOffered?.includes(service.value);
                      return (
                        <label
                          key={service.value}
                          className={cn(
                            'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                            isSelected
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 hover:border-gray-300'
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleServiceToggle(service.value)}
                            className="w-4 h-4 rounded border-gray-300 accent-teal-600"
                          />
                          <span className={cn(
                            'text-sm',
                            isSelected ? 'text-teal-700 font-medium' : 'text-gray-700'
                          )}>
                            {service.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  
                  {/* Skilled Services Notice */}
                  {agencyProfile.providesSkilled && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-amber-800">
                            Skilled Services Selected
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            Agencies providing skilled services (nursing, PT, OT, ST) have additional federal requirements under 42 CFR Part 484.
                            {!agencyProfile.medicareCertified && (
                              <span className="block mt-1 font-medium">
                                → You indicated you're not Medicare certified. Is this correct?
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Survey History */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    Survey History
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Last Survey Date"
                      type="date"
                      value={agencyProfile.lastSurveyDate || ''}
                      onChange={e => setAgencyProfile(p => ({ ...p, lastSurveyDate: e.target.value }))}
                      helpText="When was your last state or CMS survey?"
                    />
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agencyProfile.hasOpenDeficiencies || false}
                          onChange={e => setAgencyProfile(p => ({ ...p, hasOpenDeficiencies: e.target.checked }))}
                          className="mt-1 w-5 h-5 rounded border-gray-300 accent-teal-600"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Open Deficiencies</span>
                          <p className="text-sm text-gray-500">
                            Do you have any unresolved survey deficiencies or active plans of correction?
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Encouragement Warnings */}
                {getProfileEncouragements().length > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">Recommended Information</p>
                        <ul className="text-sm text-amber-700 mt-1 space-y-1">
                          {getProfileEncouragements().map((warning, idx) => (
                            <li key={idx}>• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

              </CardContent>
              <CardFooter className="flex-col-reverse sm:flex-row justify-between gap-3">
                <Button
                  variant="ghost"
                  onClick={() => goToStep('state')}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  Back
                </Button>
                <Button
                  onClick={() => {
                    if (validateProfile()) {
                      goToStep('diagnostic');
                    }
                  }}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">Next: Initial Assessment</span>
                  <span className="sm:hidden">Next</span>
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* ============================================ */}
          {/* STEP 3: INITIAL DIAGNOSTIC */}
          {/* ============================================ */}
          
          {currentStep === 'diagnostic' && (
            <Card>
              <CardHeader>
                <CardTitle>Initial Assessment</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Answer these diagnostic questions to help us assess your compliance posture for{' '}
                  <strong>{getStateOptions().find(s => s.value === agencyProfile.state)?.label}</strong>.
                </p>
              </CardHeader>
              <CardContent>
                <OnboardingDiagnostic
                  stateCode={agencyProfile.state || 'VA'}
                  answers={onboardingDiagnosticAnswers}
                  onChange={(questionId, value) => {
                    setOnboardingDiagnosticAnswers(prev => ({
                      ...prev,
                      [questionId]: value,
                    }));
                  }}
                  onProgressChange={setDiagnosticTotals}
                />

                {/* Soft validation warning */}
                {diagnosticTotals.answered < diagnosticTotals.total && diagnosticTotals.total > 0 && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">
                          {diagnosticTotals.total - diagnosticTotals.answered} question{diagnosticTotals.total - diagnosticTotals.answered !== 1 ? 's' : ''} unanswered
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          Completing all diagnostic questions helps us provide a more accurate compliance assessment.
                          You can continue to the checklist, but we recommend answering all questions.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex-col-reverse sm:flex-row justify-between gap-3">
                <Button
                  variant="ghost"
                  onClick={() => goToStep('profile')}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  Back
                </Button>
                <Button
                  onClick={() => goToStep('checklist')}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">Next: Compliance Checklist</span>
                  <span className="sm:hidden">Next</span>
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* ============================================ */}
          {/* STEP 4: CHECKLIST */}
          {/* ============================================ */}
          
          {currentStep === 'checklist' && currentCategory && (
            <div className="space-y-6">
              {/* Category Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
                {checklist.map((cat, index) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCurrentCategoryIndex(index);
                      window.scrollTo({ top: 0, behavior: 'instant' });
                    }}
                    className={cn(
                      'px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors shrink-0',
                      currentCategoryIndex === index
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    <span className="sm:hidden">{cat.name.split('. ')[0]}</span>
                    <span className="hidden sm:inline">{cat.name.split('. ')[0]}. {cat.name.split('. ')[1]?.slice(0, 15)}...</span>
                  </button>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{currentCategory.name}</CardTitle>
                  {currentCategory.description && (
                    <p className="text-sm text-gray-500 mt-1">{currentCategory.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentCategory.items.map(item => {
                    const baseItemId = item.id.includes('-') ? item.id.split('-').slice(1).join('-') : item.id;
                    const diagnosticQuestion = getDiagnosticQuestionByItemId(baseItemId, agencyProfile.state || 'VA');

                    if (diagnosticQuestion) {
                      return (
                        <MultiPartQuestionCard
                          key={item.id}
                          question={diagnosticQuestion}
                          answers={diagnosticAnswers[item.id] || []}
                          onChange={(answers) => handleDiagnosticAnswerChange(item.id, answers)}
                        />
                      );
                    }

                    const isItemExpanded = expandedChecklistItemId === item.id;
                    return (
                      <div
                        key={item.id}
                        className="p-4 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-start gap-4 mb-3">
                          <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                            {item.id.split('-').pop()}
                          </span>
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{item.text}</p>
                            {isItemExpanded && item.helpText && (
                              <p className="text-sm text-gray-500 mt-1">{item.helpText}</p>
                            )}
                          </div>
                          <span
                            className={cn(
                              'px-2 py-1 text-xs font-medium rounded',
                              item.severity === 'high'
                                ? 'bg-red-100 text-red-700'
                                : item.severity === 'medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-blue-100 text-blue-700'
                            )}
                          >
                            {item.severity}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                          {STATUS_OPTIONS.map(option => {
                            const isSelected = responses[item.id]?.status === option.value;
                            return (
                              <button
                                key={option.value}
                                onClick={() => handleResponseChange(item.id, option.value)}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all',
                                  isSelected
                                    ? option.bgColor + ' border-current'
                                    : 'bg-white border-gray-200 hover:border-gray-300'
                                )}
                              >
                                <option.icon className={cn('w-4 h-4', isSelected ? option.color : 'text-gray-400')} />
                                <span className={cn('text-sm font-medium', isSelected ? option.color : 'text-gray-600')}>
                                  {option.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {isItemExpanded && (
                          <Input
                            placeholder="Add notes (optional)"
                            value={responses[item.id]?.notes || ''}
                            onChange={e => handleNotesChange(item.id, e.target.value)}
                            onFocus={() => setExpandedChecklistItemId(item.id)}
                          />
                        )}
                      </div>
                    );
                  })}
                  {/* Category Progress Warning */}
                  {(() => {
                    const categoryUnanswered = currentCategory.items.filter(
                      item => !responses[item.id]?.status
                    ).length;
                    if (categoryUnanswered > 0) {
                      return (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <p className="font-medium text-amber-800">
                              {categoryUnanswered} item{categoryUnanswered !== 1 ? 's' : ''} in this section need a status
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
                <CardFooter className="justify-between flex-col sm:flex-row gap-4">
                  {/* Overall Progress */}
                  <div className="text-sm text-gray-500 w-full sm:w-auto">
                    <span className={cn(
                      'font-medium',
                      progress.answered >= progress.total ? 'text-green-600' : 'text-amber-600'
                    )}>
                      {progress.answered}/{progress.total}
                    </span>{' '}
                    checklist items completed
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto justify-between sm:justify-end items-center">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (currentCategoryIndex > 0) {
                          setCurrentCategoryIndex(i => i - 1);
                          window.scrollTo({ top: 0, behavior: 'instant' });
                        } else {
                          goToStep('diagnostic');
                        }
                      }}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      <span className="hidden sm:inline">{currentCategoryIndex > 0 ? 'Previous' : 'Back'}</span>
                      <span className="sm:hidden">{currentCategoryIndex > 0 ? 'Prev' : 'Back'}</span>
                    </Button>
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="ghost"
                        leftIcon={<Save className="w-4 h-4" />}
                        onClick={handleManualSave}
                        isLoading={isSaving}
                      >
                        Save
                      </Button>
                      {currentCategoryIndex < checklist.length - 1 ? (
                        <Button
                          onClick={() => {
                            setCurrentCategoryIndex(i => i + 1);
                            window.scrollTo({ top: 0, behavior: 'instant' });
                          }}
                          rightIcon={<ChevronRight className="w-4 h-4" />}
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            if (progress.answered < progress.total) {
                              showError(`Please complete all ${progress.total - progress.answered} remaining checklist items before reviewing.`);
                              return;
                            }
                            goToStep('review');
                          }}
                          rightIcon={<ChevronRight className="w-4 h-4" />}
                          disabled={progress.answered < progress.total}
                          className="whitespace-nowrap"
                        >
                          <span className="hidden sm:inline">Review & Submit</span>
                          <span className="sm:hidden">Review</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardFooter>
              </Card>
            </div>
          )}

          {/* ============================================ */}
          {/* STEP 5: REVIEW */}
          {/* ============================================ */}
          
          {currentStep === 'review' && (
            <Card>
              <CardHeader>
                <CardTitle>Review & Submit</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Review your assessment before submitting. You'll see your risk score instantly, and the full PDF report is emailed to you.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Progress Status */}
                <div className={cn(
                  'p-4 rounded-lg',
                  progress.answered >= progress.total
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-amber-50 border border-amber-200'
                )}>
                  <h3 className={cn(
                    'font-semibold mb-2',
                    progress.answered >= progress.total ? 'text-green-900' : 'text-amber-900'
                  )}>
                    {progress.answered >= progress.total 
                      ? '✓ Assessment Complete' 
                      : '⚠ Assessment Incomplete'
                    }
                  </h3>
                  <p className={progress.answered >= progress.total ? 'text-green-700' : 'text-amber-700'}>
                    You've completed {progress.answered} of {progress.total} checklist items.
                    {progress.answered < progress.total && (
                      <span className="block mt-1">
                        Please complete all items before submitting.
                      </span>
                    )}
                  </p>
                </div>

                {/* UNKNOWN Items Warning */}
                {(() => {
                  const unknownCount = getUnknownCount(responses, checklist);
                  if (unknownCount > 0) {
                    const unknownItems = getUnknownItems(responses, checklist);
                    return (
                      <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                        <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                          <HelpCircle className="w-4 h-4" />
                          {unknownCount} Item{unknownCount > 1 ? 's' : ''} Need Verification
                        </h3>
                        <p className="text-yellow-700 text-sm mb-3">
                          The following items couldn't be fully assessed based on your answers.
                          Your report will include specific guidance on what documentation to gather.
                        </p>
                        <div className="max-h-40 overflow-y-auto text-sm">
                          <ul className="space-y-1">
                            {unknownItems.slice(0, 10).map(item => (
                              <li key={item.id} className="flex items-start gap-2 text-yellow-800">
                                <span className="text-yellow-500 mt-0.5">•</span>
                                <span><strong>{item.id.split('-').pop()}</strong>: {item.text.substring(0, 60)}{item.text.length > 60 ? '...' : ''}</span>
                              </li>
                            ))}
                            {unknownItems.length > 10 && (
                              <li className="text-yellow-600 italic">
                                ...and {unknownItems.length - 10} more items
                              </li>
                            )}
                          </ul>
                        </div>
                        <p className="text-yellow-700 text-xs mt-3">
                          Tip: Go back to the checklist to review and update these items for a more accurate report.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Agency Summary */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-teal-600" />
                      Agency Information
                    </h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Name</dt>
                        <dd className="text-gray-900 font-medium">{agencyProfile.name || '—'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">State</dt>
                        <dd className="text-gray-900 font-medium">
                          {getStateOptions().find(s => s.value === agencyProfile.state)?.label || '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Staff</dt>
                        <dd className="text-gray-900 font-medium">{agencyProfile.numberOfStaff || '—'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Years Operating</dt>
                        <dd className="text-gray-900 font-medium">{agencyProfile.yearsInOperation || '—'}</dd>
                      </div>
                      {stateRequiresLicense && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">License #</dt>
                          <dd className="text-gray-900 font-medium">{agencyProfile.licenseNumber || '—'}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-teal-600" />
                      Certifications & Services
                    </h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Medicare Certified</dt>
                        <dd className="text-gray-900 font-medium">
                          {agencyProfile.medicareCertified ? 'Yes' : 'No'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Medicaid Provider</dt>
                        <dd className="text-gray-900 font-medium">
                          {agencyProfile.medicaidProvider ? 'Yes' : 'No'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Skilled Services</dt>
                        <dd className="text-gray-900 font-medium">
                          {agencyProfile.providesSkilled ? 'Yes' : 'No'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Services</dt>
                        <dd className="text-gray-900 font-medium text-right">
                          {agencyProfile.servicesOffered?.length || 0} selected
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Response Summary */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Checklist Summary</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    {STATUS_OPTIONS.map(option => {
                      const count = Object.values(responses).filter(r => r.status === option.value).length;
                      return (
                        <div key={option.value} className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                          <option.icon className={cn('w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2', option.color)} />
                          <div className="text-xl sm:text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-xs text-gray-500">{option.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Delivery Notice */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800">Report Delivery</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Your risk score appears instantly on the results page. The full PDF report is emailed to {' '}<strong>{agencyProfile.contactEmail}</strong> within minutes.
                      </p>
                    </div>
                  </div>
                </div>

              </CardContent>
              <CardFooter className="flex-col-reverse sm:flex-row justify-between gap-3">
                <Button
                  variant="ghost"
                  onClick={() => goToStep('checklist')}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">Back to Checklist</span>
                  <span className="sm:hidden">Back</span>
                </Button>
                <Button
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  disabled={progress.answered < progress.total}
                  rightIcon={<Send className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  Submit Assessment
                </Button>
              </CardFooter>
            </Card>
          )}

        </Container>
      </main>

      {/* State Change Confirmation Modal */}
      <ConfirmModal
        isOpen={stateChangeConfirm.isOpen}
        onClose={() => setStateChangeConfirm({ isOpen: false, fromState: '', toState: '' })}
        onConfirm={confirmStateChange}
        title="Reset Assessment?"
        variant="warning"
        confirmText="Yes, Change State"
        cancelText="Keep Current State"
        message={
          <div className="space-y-3">
            <p>
              Changing your state from{' '}
              <strong>{getStateOptions().find(s => s.value === stateChangeConfirm.fromState)?.label}</strong> to{' '}
              <strong>{getStateOptions().find(s => s.value === stateChangeConfirm.toState)?.label}</strong> will reset your assessment.
            </p>
            <p>
              Each state has different compliance requirements, so your previous answers cannot be transferred.
            </p>
          </div>
        }
      />
    </div>
  );
}

export default Assessment;